"use client";

import { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

// Engrenage + popover des préférences d'APPRENTISSAGE, calqué pixel pour pixel
// sur le popover du son qui vit dans la même barre. L'état appartient au
// player (qui le lit pour corriger et requeuer) : ici on ne fait qu'afficher
// et rappeler — aucune persistance locale, c'est lib/reps/learningPrefs.js.
function ToggleRow({ label, description, checked, onChange }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={() => onChange(!checked)}
            className="flex w-full items-start gap-3 rounded-xl px-2 py-2 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.04]"
        >
            <span
                className={`relative mt-0.5 inline-flex h-5 w-9 shrink-0 rounded-full transition-colors ${
                    checked ? "bg-primary" : "bg-gray-200 dark:bg-white/15"
                }`}
            >
                <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                        checked ? "translate-x-[18px]" : "translate-x-0.5"
                    }`}
                />
            </span>
            <span className="min-w-0">
                <span className="block text-[13px] font-semibold leading-tight text-gray-800 dark:text-grayDark-100">
                    {label}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-gray-500 dark:text-grayDark-400">
                    {description}
                </span>
            </span>
        </button>
    );
}

export default function RepsLearningSettings({ prefs, onChange }) {
    const t = useTranslations();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        function onDoc(e) {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        }
        function onEsc(e) {
            if (e.key === "Escape") setOpen(false);
        }
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onEsc);
        };
    }, [open]);

    return (
        <div className="relative shrink-0" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-label={t("reps_learning_settings")}
                className="flex text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-white"
            >
                <SolarIcon name="settings-linear" className="text-xl" />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96, y: -4 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.96, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full z-[70] mt-2 w-72 rounded-2xl border border-gray-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-bgDark-840"
                    >
                        <p className="mb-2 px-2 text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-grayDark-500">
                            {t("reps_learning_settings")}
                        </p>
                        <ToggleRow
                            label={t("reps_pref_show_answers")}
                            description={t("reps_pref_show_answers_desc")}
                            checked={prefs.showAnswers}
                            onChange={(v) => onChange({ showAnswers: v })}
                        />
                        <ToggleRow
                            label={t("reps_pref_insist")}
                            description={t("reps_pref_insist_desc")}
                            checked={prefs.insistUntilCorrect}
                            onChange={(v) => onChange({ insistUntilCorrect: v })}
                        />
                        <ToggleRow
                            label={t("reps_pref_read_along")}
                            description={t("reps_pref_read_along_desc")}
                            checked={prefs.readAlong}
                            onChange={(v) => onChange({ readAlong: v })}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
